<template>
  <a-spin :spinning="spinning">
    <div :style="outerCardStyle">
      <div class="outer-title-bar">
        <span style="display: flex; align-items: center;">
          <span :style="pieCardIconStyle">
            <component :is="TeamOutlined" />
          </span>
          <span class="pies-and-table-card-title">OpenHarmony PMC（包含主干、SIG、TPC）</span>
        </span>
      </div>
      <a-row :gutter="16" justify="center">
        <a-col v-for="(stat, index) in stats" :key="index" :span="6">
          <!-- 统计卡片 -->
          <div :style="statStyle">
            <!-- 图标和内容并列 -->
            <div :style="iconStyle">
              <component :is="stat.icon" />
            </div>
            <!-- 统计内容 -->
            <div class="stat-content">
              <div class="stat-title">{{ stat.title }}</div>
              <div class="stat-value-row">
                <!-- TOP贡献特殊处理 -->
                <div v-if="stat.title === 'TOP 贡献'" class="contributor-name">
                  {{ stat.value }}
                </div>
                <div v-else>
                  <div class="stat-value">{{ formatNumber(stat.value) }}</div>
                </div>
                <span class="stat-suffix">{{ stat.suffix }}</span>
              </div>
              <!-- TOP贡献者代码量 -->
              <div v-if="stat.title === 'TOP 贡献'" class="stat-code-amount">
                代码量：{{ formatNumber(topContributorCodeLines) }} 行
              </div>
            </div>
          </div>
        </a-col>
      </a-row>

      <div :style="pieCardStyle">
      <div class="pie-and-table-cards-flex">
        <div class="pie-and-table-card" style="flex: 0 0 35%;">
          <div class="pie-title-container">
            <div class="pie-title">华为 vs 非华为</div>
          </div>
          <div style="height: 100%;">
            <div style="height: 200px; margin-bottom: 12px;">
              <my-pie-chart :data="leftPieData.contributor" :colors="huaweiNonHuaweiColors" suffix="人" />
            </div>
            <div style="display: flex;">
              <div v-for="(data, index) in leftPieData.contributor" :key="index" class="pie-data-box"
                :style="{ borderColor: huaweiNonHuaweiColors[index] }">
                <div class="pie-data-title" style="margin-bottom: 6px;">
                  <span class="pie-data-dot" :style="{ color: huaweiNonHuaweiColors[index] }">●</span>
                  {{ data.name }}
                </div>
                <span style="font-size: 16px; font-weight: 600; ">
                  {{ formatNumber(data.value) }}
                </span>
                <span class="pie-data-suffix"> 人</span>
                <div style="font-size: 12px; color: #666; margin-top: 5px; ">
                  占比 {{((data.value / leftPieData.contributor.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(2)}}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pie-and-table-card" style="flex: 0 0 65%;">
          <div class="pie-title-container">
            <div class="pie-title">新增贡献趋势</div>
          </div>
          <div class="trend-row">
            <div class="trend-chart-wrap">
              <a-spin :spinning="loadingNew" tip="加载中...">
                <div ref="newContributorChartRef" class="contributor-change-chart"></div>
              </a-spin>
              <div class="chart-legend">
                <span class="legend-item"><span class="legend-dot legend-dot-non-huawei"></span>非华为</span>
                <span class="legend-item"><span class="legend-dot legend-dot-huawei"></span>华为</span>
                <span class="legend-item"><span class="legend-dot legend-dot-total"></span>总数</span>
              </div>
            </div>
            <div class="trend-table-wrap">
              <table class="contributor-table">
                <thead>
                  <tr>
                    <th>类别</th>
                    <th v-for="year in newTableYears" :key="year">{{ year }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in newContributorTableData" :key="row.key">
                    <td>{{ row.category }}</td>
                    <td v-for="year in newTableYears" :key="year">{{ formatNumber(row[year]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div :style="pieCardStyle" class="no-padding-card">
        <summary-report-pr />
      </div>

      <div :style="pieCardStyle" class="no-padding-card">
        <summary-report-pr-top />
      </div>

      <div :style="pieCardStyle" class="no-padding-card">
        <summary-report-issue />
      </div>

      <div :style="pieCardStyle" class="no-padding-card">
        <summary-report-issue-top />
      </div>
    </div>
    </div>
  </a-spin>

</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import * as echarts from 'echarts';
import { TeamOutlined, CodeOutlined, EnvironmentOutlined, ApartmentOutlined } from '@ant-design/icons-vue';
import MyPieChart from '../components/MyPieChart.vue';
import SummaryReportPr from './SummaryReportPr.vue';
import SummaryReportIssue from './SummaryReportIssue.vue';
import SummaryReportPrTop from './SummaryReportPrTop.vue';
import SummaryReportIssueTop from './SummaryReportIssueTop.vue';

/* global defineProps */
const props = defineProps({
  dimension: {
    type: String,
    default: 'all'
  }
});

const THEME_COLOR = '#50A2FF';

// ----- Constants & lookups -----
const requestParamProjectMap = { all: '', master: 'openharmony', sig: 'openharmony-sig', tpc: 'openharmony-tpc' };
const nonHuaweiOrder = ['HABO', 'DONATE', 'PARTNER', 'SCHOOL', 'OTHERS'];
const nonHuaweiNameMap = { HABO: '伙伴：哈勃', DONATE: '伙伴：捐赠单位', PARTNER: '伙伴：其他', SCHOOL: '高校', OTHERS: '其他' };
const huaweiNonHuaweiColors = ['#EF4444', '#3B82F6'];

// ----- Helpers -----
const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const buildNonHuaweiContributor = (situations) => {
  // 依据固定顺序把后端枚举映射成饼图数据
  const contributorArr = [];
  for (const key of nonHuaweiOrder) {
    const item = situations.find((s) => s.remarks === key);
    if (item) contributorArr.push({ name: nonHuaweiNameMap[key], value: item.number });
  }
  return contributorArr;
};

const buildNonHuaweiCode = (situations) => {
  const codeArr = [];
  for (const key of nonHuaweiOrder) {
    const item = situations.find((s) => s.remarks === key);
    if (item) codeArr.push({ name: nonHuaweiNameMap[key], value: item.codeLines });
  }
  return codeArr;
};

const getSituationCodeLines = (situations, remark) => {
  const item = situations.find((s) => s.remarks === remark);
  return item?.codeLines ?? 0;
};

const loadingNew = ref(false);
const newContributorChartRef = ref(null);
let newContributorChartInstance = null;
const newYears = ref(['2020年', '2021年', '2022年', '2023年', '2024年', '2025年', '2026年']);
const newTableYears = ref(['2020', '2021', '2022', '2023', '2024', '2025', '2026']);
const newContributorChartData = ref({ total: [], huawei: [], nonHuawei: [] });
const newContributorTableData = ref([]);

const transformApiData = (apiData = []) => {
  const huawei = [];
  const nonHuawei = [];
  const total = [];
  const yearsArr = [];
  if (!Array.isArray(apiData)) return { huawei, nonHuawei, total, yearsArr };
  apiData.forEach((item) => {
    const year = Number(item.year);
    yearsArr.push(`${year}年`);
    huawei.push(Number(item.huaweiNum || 0));
    nonHuawei.push(Number(item.nonHuaweiNum || 0));
    total.push(Number(item.totalNum || 0));
  });
  return { huawei, nonHuawei, total, yearsArr };
};

const chartToTable = (chart, tableYearsArr) => {
  const buildRow = (key, category, values) => ({
    key,
    category,
    ...tableYearsArr.reduce((acc, year, index) => {
      acc[year] = values[index] ?? 0;
      return acc;
    }, {})
  });
  return [
    buildRow('total', '总数', chart.total),
    buildRow('huawei', '华为', chart.huawei),
    buildRow('nonHuawei', '非华为', chart.nonHuawei)
  ];
};

const renderNewChart = () => {
  if (!newContributorChartRef.value) return;
  if (newContributorChartInstance) newContributorChartInstance.dispose();
  newContributorChartInstance = echarts.init(newContributorChartRef.value);
  const option = {
    tooltip: { trigger: 'axis' },
    legend: { show: false },
    grid: { left: '3%', right: '4%', top: '5%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: newYears.value,
      axisLine: { lineStyle: { color: '#e5e7eb' } }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      splitLine: { lineStyle: { color: '#e5e7eb' } }
    },
    series: [
      {
        name: '非华为',
        type: 'bar',
        stack: 'total',
        itemStyle: { color: '#9ca3af' },
        data: newContributorChartData.value.nonHuawei
      },
      {
        name: '华为',
        type: 'bar',
        stack: 'total',
        itemStyle: { color: '#3b82f6' },
        data: newContributorChartData.value.huawei
      },
      {
        name: '总数',
        type: 'line',
        smooth: true,
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 2 },
        symbol: 'circle',
        symbolSize: 8,
        data: newContributorChartData.value.total
      }
    ]
  };
  newContributorChartInstance.setOption(option);
};

const getNewContributionData = async () => {
  loadingNew.value = true;
  try {
    const project = requestParamProjectMap[props.dimension] || '';
    const res = await axios.get('/contributorController/getNewContributionTrend', { params: { project } });
    if (res.data.code === 200) {
      const chart = transformApiData(res.data.data);
      newContributorChartData.value = { huawei: chart.huawei, nonHuawei: chart.nonHuawei, total: chart.total };
      newYears.value = chart.yearsArr.length ? chart.yearsArr : newYears.value;
      newTableYears.value = newYears.value.map((y) => y.replace('年', ''));
      newContributorTableData.value = chartToTable(newContributorChartData.value, newTableYears.value);
    }
  } catch {
    newContributorChartData.value = { total: [], huawei: [], nonHuawei: [] };
    newContributorTableData.value = [];
  } finally {
    loadingNew.value = false;
    await nextTick();
    renderNewChart();
  }
};

// ----- State -----
const spinning = ref(false);
const cache = ref({ all: null, master: null, sig: null, tpc: null });

// ----- Derived state -----
const currentCache = computed(() => cache.value[props.dimension] || {});
const stats = computed(() => [{
  title: '贡献者数量',
  value: currentCache.value.totalNum,
  suffix: '人',
  icon: TeamOutlined
}, {
  title: '代码量',
  value: currentCache.value.totalCodeLines,
  suffix: '行',
  icon: CodeOutlined
}, {
  title: '组织数量',
  value: currentCache.value.organizationNum,
  suffix: '家',
  icon: EnvironmentOutlined
}, {
  title: 'SIG总数',
  value: 73,
  suffix: '个',
  icon: ApartmentOutlined
}]);
const topContributorCodeLines = computed(() => currentCache.value.topContributorCodeLines);
const leftPieData = computed(() => currentCache.value.leftPieData || { contributor: [], code: [] });

// ----- API fetchers -----
const fetchData = async (val) => {
  const project = requestParamProjectMap[val];
  const res = await axios.get('/metric/getOverallSituation', {
    params: { project: project }
  });
  const rankRes = await axios.get('/metric/contributorRank', {
    params: { project: project }
  });
  if (res.data.code === 200 && rankRes.data.code === 200) {
    const data = res.data.data;
    const rankData = rankRes.data.data;
    cache.value[val] = {
      totalNum: data.totalNum,
      totalCodeLines: data.totalCodeLines,
      organizationNum: data.organizationNum,
      topContributor: rankData[0].contributor,
      topContributorCodeLines: rankData[0].codeLines,
      leftPieData: {
        contributor: [
          { name: '华为', value: data.huaweiNum },
          { name: '非华为', value: data.nonHuaweiNum }
        ],
        code: [
          { name: '华为', value: getSituationCodeLines(data.situations || [], 'HUAWEI') },
          { name: '非华为', value: (data.totalCodeLines || 0) - getSituationCodeLines(data.situations || [], 'HUAWEI') }
        ]
      },
      rightPieData: {
        contributor: buildNonHuaweiContributor(data.situations || []),
        code: buildNonHuaweiCode(data.situations || [])
      }
    };
  } else {
    throw new Error(res.data?.msg || '请求失败');
  }
};

const loadData = async (val) => {
  if (cache.value[val]) return;

  spinning.value = true;
  try {
    await fetchData(val);
  } catch (error) {
    notification.error({ message: '数据加载失败', description: error?.message || error });
  } finally {
    spinning.value = false;
  }
};

// ----- Watchers & lifecycle -----
watch(() => props.dimension, async (dimension) => {
  await loadData(dimension);
});

onMounted(async () => {
  await loadData(props.dimension);
  await getNewContributionData();
  window.addEventListener('resize', () => newContributorChartInstance?.resize());
});

// ----- Styles -----
const outerCardStyle = computed(() => ({
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  padding: '16px',
  borderRadius: '12px'
}));

const statStyle = computed(() => ({
  backgroundColor: '#F9FAFB',
  padding: '12px',
  borderRadius: '12px',
  minHeight: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
}));

const iconStyle = computed(() => ({
  width: '44px',
  height: '44px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: THEME_COLOR + '14',
  color: THEME_COLOR,
  fontSize: '18px',
  marginRight: '12px'
}));

const pieCardIconStyle = computed(() => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: THEME_COLOR + '14',
  color: THEME_COLOR,
  fontSize: '16px',
  marginRight: '8px'
}));

const pieCardStyle = computed(() => ({
  backgroundColor: '#fff',
  padding: '16px 24px 0 24px',
  borderRadius: '12px',
  margin: '16px 0 16px 0'
}));
</script>

<style scoped>
.no-padding-card {
  padding: 0 !important;
}

.outer-title-bar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-content {
  flex: 1;
  text-align: center;
}

.stat-title {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin-bottom: 4px;
}

.stat-value-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.stat-value {
  font-size: 22px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1;
}

.stat-suffix {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: 6px;
}

.contributor-name {
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
  word-break: break-all;
}

.stat-code-amount {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 4px;
}

.pies-and-table-card-title {
  font-size: 16px;
  font-weight: 600;
}

.pie-and-table-card {
  flex: 1;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 16px 16px 16px 16px;
  background-color: #f9fafb;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
}

.pie-and-table-cards-flex {
  display: flex;
  gap: 16px;
}

.pie-data-box {
  background-color: #fff;
  border: 2px solid;
  border-radius: 12px;
  padding: 8px 12px;
  width: 100%;
  flex: 1;
  margin: 0 8px;
  background-clip: padding-box;
  align-items: center;
  text-align: left;
}

.pie-data-title {
  font-size: 14px;
  display: flex;
  align-items: baseline;
  font-weight: 500;
}

.pie-data-dot {
  font-weight: 600;
  font-family: 'MS Mincho', 'SimSun', 'serif';
  margin-right: 4px;
}

.pie-data-suffix {
  font-size: 12px;
  margin-top: 4px;
}

.pie-data-total-box {
  border-color: #95A5F5;
  background-color: #EFF6FF;
  border-width: 2px;
  color: #4338CA;
  display: flex;
  justify-content: space-between;
  padding: 5px 12px;
  box-shadow: 0 0 12px 2px #95A5F533;
}

.pie-title-container {
  margin-bottom: 8px;
  padding-left: 16px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.pie-title {
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
}

.trend-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.trend-chart-wrap {
  flex: 1;
  min-width: 0;
}

.trend-table-wrap {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  margin-top: 40px;
}

.contributor-change-chart {
  width: 100%;
  height: 280px;
}

.chart-legend {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot-non-huawei { background-color: #9ca3af; }
.legend-dot-huawei { background-color: #3b82f6; }
.legend-dot-total { background-color: #10b981; }

.contributor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  table-layout: fixed;
  min-width: 400px;
}

.contributor-table th,
.contributor-table td {
  padding: 10px 14px;
  text-align: center;
}

.contributor-table th {
  background: linear-gradient(135deg, #f0f7ff 0%, #f5f3ff 100%);
  font-weight: 600;
  color: #1e3a5f;
  border-bottom: 2px solid #c7d2fe;
}

.contributor-table td:first-child {
  font-weight: 600;
  color: #1e3a5f;
  background: #f9fafb;
  text-align: left;
  padding-left: 16px;
  white-space: nowrap;
  width: 80px;
}

.contributor-table th:first-child {
  width: 80px;
  text-align: left;
  padding-left: 16px;
}

.contributor-table tbody tr:nth-child(even) {
  background: #fafbfc;
}

.contributor-table tbody tr:hover {
  background: #f0f7ff;
}

.sig-overview-container {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 18px;
  margin-top: 16px;
}

.sig-overview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.sig-overview-icon {
  font-size: 18px;
  color: #50A2FF;
}

.sig-overview-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.sig-overview-body {
  display: flex;
  gap: 24px;
}

.sig-overview-main {
  flex: 1;
}

.sig-stat-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.sig-stat-card {
  flex: 1;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 20px;
}

.sig-stat-has-repo {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.sig-stat-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}

.sig-stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
}

.sig-stat-value-green {
  color: #16a34a;
}

.sig-progress-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sig-progress-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sig-progress-label {
  font-size: 14px;
  color: #374151;
  min-width: 120px;
  font-weight: 500;
}

.sig-progress-bar-bg {
  flex: 1;
  height: 10px;
  background: #e5e7eb;
  border-radius: 5px;
  overflow: hidden;
}

.sig-progress-bar {
  height: 100%;
  border-radius: 5px;
}

.sig-progress-bar-purple {
  background: linear-gradient(90deg, #a78bfa, #c084fc);
}

.sig-progress-bar-blue {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.sig-progress-bar-gray {
  background: #d1d5db;
}

.sig-progress-count {
  font-size: 14px;
  font-weight: 600;
  min-width: 48px;
  text-align: right;
}

.sig-progress-count-purple {
  color: #7c3aed;
}

.sig-progress-count-blue {
  color: #2563eb;
}

.sig-progress-count-gray {
  color: #6b7280;
}

.top-active-container {
  margin-top: 16px;
}

.top-active-row {
  display: flex;
  gap: 16px;
}

.top-active-card {
  flex: 1;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 18px;
}

.top-active-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.top-active-icon {
  font-size: 16px;
}

.top-active-icon-purple {
  color: #7c3aed;
}

.top-active-icon-blue {
  color: #2563eb;
}

.top-active-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.top-active-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.top-active-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.top-active-item-purple {
  background: #f5f3ff;
}

.top-active-item-blue {
  background: #eff6ff;
}

.top-active-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.top-active-rank-purple {
  background: #a78bfa;
}

.top-active-rank-blue {
  background: #60a5fa;
}

.top-active-name {
  font-weight: 500;
  color: #374151;
}
</style>